import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { CourseValidationErrors } from "@src/features/libraryItem/course/types/validation";
import useCourseCommentThreads from "@src/features/libraryItem/course/hooks/useCourseCommentThreads";
import useUpdateAILessons from "@src/features/libraryItem/course/hooks/useUpdateAiLessons";
import useCourseVersionAIRequests from "@src/features/libraryItem/course/hooks/useCourseVersionAIRequests";
import { CourseContentCourseVersionFragment } from "../build/CourseBuildContent.generated";
import {
  PromptTab,
  useLibraryItemQueryParams,
} from "../../contexts/LibraryItemQueryParamsContext";
import useContentPermissions from "../../hooks/useContentPermissions";
import useViewModeQueryParams from "../../hooks/useViewModeQueryParams";
import useVersionQueryParams from "../../hooks/useVersionQueryParams";
import useCourseBuilderStore, {
  getCourseBuilderState,
  selectActiveScreen,
  selectCourseId,
  selectCourseVersionId,
} from "../stores/useCourseBuilderStore";

type SetCourseValidationErrorsFn = (
  fn: (oldValidationErrors: CourseValidationErrors) => CourseValidationErrors,
) => void;

export type ScrollDirection = "up" | "down" | "none";

type Props = {
  newCourseVersion?: CourseContentCourseVersionFragment | null | undefined;
  setValidationErrors?: SetCourseValidationErrorsFn;
  marketplaceModule?: boolean;
  children?: React.ReactElement;
};

// Course data and selection live in useCourseBuilderStore. This context only
// carries actions that also write the URL, plus page-level flags; its value is
// stable for the lifetime of the builder.
export interface CourseBuilderContextState {
  setValidationErrors?: SetCourseValidationErrorsFn;
  setActiveScreenId: (promptUuid: string, promptTab?: PromptTab) => void;
  selectNextScreen: () => void;
  selectPrevScreen: () => void;
  marketplaceModule: boolean;
}

export const CourseBuilderContext = React.createContext<
  CourseBuilderContextState | undefined
>(undefined);

const CourseBuilderStateProvider: FC<Props> = (props) => {
  const courseVersionId = useCourseBuilderStore(selectCourseVersionId);
  const courseId = useCourseBuilderStore(selectCourseId);
  const { selectedPromptId, setPromptId, selectedPromptTab } =
    useLibraryItemQueryParams();
  const { viewMode } = useViewModeQueryParams();
  const { version } = useVersionQueryParams();
  useLayoutEffect(() => {
    getCourseBuilderState().syncFromUrl({
      activeScreenId: selectedPromptId,
      promptTab: selectedPromptTab,
      viewMode: viewMode ?? "medium-screens",
      version,
    });
  }, [selectedPromptId, selectedPromptTab, version, viewMode]);
  const commentThreadsArgs = useMemo(
    () => ({ courseVersionId }),
    [courseVersionId],
  );
  const { commentThreads } = useCourseCommentThreads(commentThreadsArgs);
  const { aiRequests } = useCourseVersionAIRequests({ courseVersionId });
  useLayoutEffect(() => {
    getCourseBuilderState().setScreenInputs({ commentThreads, aiRequests });
  }, [aiRequests, commentThreads]);
  const permissions = useContentPermissions({ courseId });
  const defaultPromptTab =
    permissions?.hasEditContentPermission || !permissions?.hasCommentPermission
      ? PromptTab.Design
      : PromptTab.Comments;
  const latestRef = useRef({ setPromptId, defaultPromptTab });
  useLayoutEffect(() => {
    latestRef.current = { setPromptId, defaultPromptTab };
  });
  const activeScreenIsMissing = useCourseBuilderStore(
    (s) =>
      !!selectedPromptId &&
      s.screens.length > 0 &&
      !s.screensById.has(selectedPromptId),
  );
  useEffect(() => {
    if (!activeScreenIsMissing) return;
    const firstScreen = getCourseBuilderState().screens[0];
    if (firstScreen) setPromptId(firstScreen.id, defaultPromptTab);
  }, [activeScreenIsMissing, defaultPromptTab, setPromptId]);
  useUpdateAILessons({
    initialOrPollingCourseVersion: props.newCourseVersion,
  });
  const selectScreenAt = useCallback((offset: 1 | -1) => {
    const state = getCourseBuilderState();
    const activeIndex = selectActiveScreen(state)?.index;
    if (activeIndex === undefined) return;
    const target = state.screens[activeIndex + offset];
    if (!target) return;
    latestRef.current.setPromptId(
      target.id,
      latestRef.current.defaultPromptTab,
    );
    state.setSelectedScreenIds([target.id]);
  }, []);
  const selectNextScreen = useCallback(
    () => selectScreenAt(1),
    [selectScreenAt],
  );
  const selectPrevScreen = useCallback(
    () => selectScreenAt(-1),
    [selectScreenAt],
  );
  const setActiveScreenId = useCallback(
    (promptUuid: string, promptTab?: PromptTab) => {
      latestRef.current.setPromptId(
        promptUuid,
        promptTab || latestRef.current.defaultPromptTab,
      );
      getCourseBuilderState().setSelectedScreenIds([promptUuid]);
    },
    [],
  );
  const contextState: CourseBuilderContextState = useMemo(
    () => ({
      marketplaceModule: props.marketplaceModule || false,
      setActiveScreenId,
      selectNextScreen,
      selectPrevScreen,
      setValidationErrors: props.setValidationErrors,
    }),
    [
      props.marketplaceModule,
      props.setValidationErrors,
      setActiveScreenId,
      selectNextScreen,
      selectPrevScreen,
    ],
  );

  return (
    <CourseBuilderContext.Provider value={contextState}>
      {props.children}
    </CourseBuilderContext.Provider>
  );
};
export default CourseBuilderStateProvider;
